import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import vehicleService from '../services/vehicle.service';
import { setMessage } from './message';

export const fetchVehicleList = createAsyncThunk(
    'vehicle/fetchVehicleList',
    async ({ page, pagesize, search, ordering, signal, requestId }, thunkAPI) => {
        try {
            const response = await vehicleService.getVehicleList(page, pagesize, search, ordering,signal);
            return { ...response.data, requestId }; // Assuming response contains { data, count, totalPages, etc. }
        } catch (error) {
            if (error.name === 'CanceledError') {
                return thunkAPI.rejectWithValue('aborted');
            }
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    },
);

export const fetchVehicleDropdown = createAsyncThunk(
    'vehicle/fetchVehicleDropdown',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await vehicleService.getVehicleDropdown(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data, count, totalPages, etc. }
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    },
);
export const fetchArchivedVehicleList = createAsyncThunk(
    'vehicle/fetchArchivedVehicleList',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await vehicleService.getVehicleArchiveList(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data, count, totalPages, etc. }
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    },
);
export const createVehicle = createAsyncThunk(
    'vehicle/createVehicle',
    async (vehicleData, thunkAPI) => {
        try {
            const response = await vehicleService.addVehicle(vehicleData);
            return response.data; // Assuming response contains the created die group data
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);
export const updateVehicle = createAsyncThunk(
    'vehicle/updateVehicle',
    async ({ id, values }, thunkAPI) => {
        try {
            const response = await vehicleService.updateVehicle(id, values);
            thunkAPI.dispatch(fetchVehicleList({ page: 1, limit: 10 })); // Re-fetch list after update
            return response.data; // Return the updated die group data
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const deleteVehicle = createAsyncThunk(
    'vehicle/deleteVehicle',
    async (id, thunkAPI) => {
        try {
            await vehicleService.deleteVehicle(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const unArchiveVehicle = createAsyncThunk(
    'vehicle/unArchiveVehicle',
    async (id, thunkAPI) => {
        try {
            await vehicleService.unArchiveVehicle(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    },
);

const initialState = {
    data: [],
    list: [],
    loading: false,
    totalpage: 0,
    currentpage: 1,
    totalRecord: 0,
    status: 'idle',
    error: null,
    hasMore: true,
};

const vehicleSlice = createSlice({
    name: 'vehicle',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        resetVehicleList: state => {
            state.list = []; // This will clear the list
        },
        resetCurrentVehiclePage: state => {
            state.currentpage = 1;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchVehicleList.pending, state => {
                state.status = 'pending';
                state.loading = true;
            })
            .addCase(fetchVehicleList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.loading = false;
                const fetchedPage = action.payload.page || state.currentpage;
                state.data = action.payload.results.data || []; // Adjust this to match your API response
                state.totalRecord = action.payload.count || 0;
                state.totalpage = action.payload.totalPages || 0;
                state.currentpage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchVehicleList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });

        builder
            .addCase(fetchVehicleDropdown.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchVehicleDropdown.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentpage;
                state.data = action.payload.results.data || []; // Adjust this to match your API response
                state.totalRecord = action.payload.count || 0;
                state.totalpage = action.payload.totalPages || 0;
                state.currentpage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchVehicleDropdown.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });


        builder
            .addCase(fetchArchivedVehicleList.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchArchivedVehicleList.fulfilled, (state, action) => {
                console.log('Archived Vehicle List Payload:', action.payload);
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentpage;
                state.data = action.payload.results.data || []; // Adjust this to match your API response
                state.totalRecord = action.payload.count || 0;
                state.totalpage = action.payload.totalPages || 0;
                state.currentpage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchArchivedVehicleList.rejected, (state, action) => {
            console.log('Error fetching archived vehicle list:', action.payload);
            
                state.status = 'failed';
                state.loading = false;
                state.data =  []; 
                state.list =  [];  
                state.error = action?.error.message;
            });
        builder
            .addCase(createVehicle.pending, state => {
                state.status = 'loading';
            })
            .addCase(createVehicle.fulfilled, (state, action) => {
                state.status = 'succeeded';
            })
            .addCase(createVehicle.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
        builder
            .addCase(updateVehicle.pending, state => {
                state.status = 'loading';
            })
            .addCase(updateVehicle.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const updatedGroup = action.payload;
                const index = state.data.findIndex(
                    group => group.id === updatedGroup.id,
                ); // Find the index of the updated group
                if (index !== -1) {
                    state.data[index] = updatedGroup; // Replace the old group with the updated one
                }
            })
            .addCase(updateVehicle.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(deleteVehicle.pending, state => {
                state.status = 'loading';
            })
            .addCase(deleteVehicle.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
                state.list = state.list.filter(group => group.id !== action.payload); // Remove the deleted group from list
            })
            .addCase(deleteVehicle.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(unArchiveVehicle.pending, state => {
                state.status = 'loading';
            })
            .addCase(unArchiveVehicle.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
            })
            .addCase(unArchiveVehicle.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

export const { resetVehicleList, resetCurrentVehiclePage } = vehicleSlice.actions;
const { reducer } = vehicleSlice;
export default reducer;
